import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { productsService } from '../services';
import { Product } from '../types/product.type';
import { ProductCategory } from '../constants/productCategory';
import { useAuth } from './AuthContext';

interface ProductsContextType {
  products: Record<ProductCategory, Product[]>;
  getProductsByCategory: (category: ProductCategory) => Product[];
  isLoading: Record<ProductCategory, boolean>;
  refreshProducts: (category: ProductCategory) => Promise<void>;
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

interface ProductsProviderProps {
  children: ReactNode;
}

export const ProductsProvider: React.FC<ProductsProviderProps> = ({ children }) => {
  const { accessToken, isLoading: authLoading } = useAuth();
  const [products, setProducts] = useState<Record<ProductCategory, Product[]>>({
    [ProductCategory.VM]: [],
    [ProductCategory.APP_DEPLOY]: [],
    [ProductCategory.BLUEPRINT]: [],
    [ProductCategory.DATABASE]: [],
    [ProductCategory.AI_SERVICE]: [],
    [ProductCategory.SITE]: [],
    [ProductCategory.RABBITMQ]: [],
    [ProductCategory.N8N]: [],
    [ProductCategory.STRAPI]: [],
    [ProductCategory.MEDUSA]: [],
    [ProductCategory.ODOO]: [],
    [ProductCategory.IDURAR]: [],
    [ProductCategory.FRAPPE_EDUCATION]: [],
    [ProductCategory.METABASE]: [],
    [ProductCategory.CLICKHOUSE]: [],
    [ProductCategory.TOOLJET]: [],
    [ProductCategory.GHOST]: [],
    [ProductCategory.REDIS]: [],
    [ProductCategory.ONYX]: [],
    [ProductCategory.DOCKER]: [],
    [ProductCategory.WORDPRESS]: [],
    [ProductCategory.SQLSERVER_EXPRESS]: [],
  });

  const [isLoading, setIsLoading] = useState<Record<ProductCategory, boolean>>({
    [ProductCategory.VM]: false,
    [ProductCategory.APP_DEPLOY]: false,
    [ProductCategory.BLUEPRINT]: false,
    [ProductCategory.DATABASE]: false,
    [ProductCategory.AI_SERVICE]: false,
    [ProductCategory.SITE]: false,
    [ProductCategory.RABBITMQ]: false,
    [ProductCategory.N8N]: false,
    [ProductCategory.STRAPI]: false,
    [ProductCategory.MEDUSA]: false,
    [ProductCategory.ODOO]: false,
    [ProductCategory.IDURAR]: false,
    [ProductCategory.FRAPPE_EDUCATION]: false,
    [ProductCategory.METABASE]: false,
    [ProductCategory.CLICKHOUSE]: false,
    [ProductCategory.TOOLJET]: false,
    [ProductCategory.GHOST]: false,
    [ProductCategory.REDIS]: false,
    [ProductCategory.ONYX]: false,
    [ProductCategory.DOCKER]: false,
    [ProductCategory.WORDPRESS]: false,
    [ProductCategory.SQLSERVER_EXPRESS]: false,
  });

  const [fetchedCategories, setFetchedCategories] = useState<Set<ProductCategory>>(new Set());

  const fetchProducts = async (category: ProductCategory) => {
    // Si ya se obtuvo esta categoría, no volver a hacer la llamada
    if (fetchedCategories.has(category)) {
      return;
    }

    // Si no hay token, esperar hasta 3 segundos antes de hacer la petición
    if (!accessToken && !authLoading) {
      console.log(`No token available for ${category}, waiting up to 3 seconds...`);
      const { waitForToken } = await import('../utils/tokenHelper');
      await waitForToken(3000, 200);
    }

    setIsLoading(prev => ({ ...prev, [category]: true }));

    try {
      const response = await productsService.getProductsByCategory(category);
      const productsArray = Array.isArray(response?.products) ? response.products : [];
      
      console.log(`${category} Products cached:`, productsArray);
      
      setProducts(prev => ({
        ...prev,
        [category]: productsArray,
      }));
      
      setFetchedCategories(prev => new Set(prev).add(category));
    } catch (error) {
      console.error(`Error fetching ${category} products:`, error);
      setProducts(prev => ({
        ...prev,
        [category]: [],
      }));
    } finally {
      setIsLoading(prev => ({ ...prev, [category]: false }));
    }
  };

  const getProductsByCategory = React.useCallback((category: ProductCategory): Product[] => {
    // Si no se han obtenido los productos de esta categoría, iniciar la carga
    if (!fetchedCategories.has(category) && !authLoading && accessToken) {
      fetchProducts(category);
    }
    return products[category] || [];
  }, [fetchedCategories, authLoading, accessToken, products]);

  const refreshProducts = async (category: ProductCategory) => {
    // Remover de fetchedCategories para forzar una nueva carga
    setFetchedCategories(prev => {
      const newSet = new Set(prev);
      newSet.delete(category);
      return newSet;
    });
    await fetchProducts(category);
  };

  return (
    <ProductsContext.Provider
      value={{
        products,
        getProductsByCategory,
        isLoading,
        refreshProducts,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = (): ProductsContextType => {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

